/**
 * Area — Documenti (PR-ONB-DOCS-2).
 *
 * Authenticated checklist of the CURRENT request's documents: per-type state
 * (Da caricare / Caricato / In verifica / Da correggere / Approvato /
 * Respinto), upload + replacement where the SERVER allows it (the FE mirrors
 * `canUpload` — policy never re-derived client-side), the BO structured
 * correction note when present. No chat. Replacement = new version (the
 * prior one stays as history server-side).
 *
 * Upload pipeline per document: client pre-check (mime/size, friendlier
 * than a server round-trip) → sign-upload (server generates the storage
 * key) → PUT to the signed URL → confirm-upload → list reload.
 *
 * The actual submit lives in the wizard summary (PR-ONB-PRECHECK-3); the
 * footer here guides the applicant back to the riepilogo to submit
 * (PR-ONB-WIZARD-AREA-UX-1) instead of a dead disabled placeholder.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner } from '@ab-platform/ui/banner';
import { FieldError } from '@ab-platform/ui/field-error';
import { FileInput } from '@ab-platform/ui/file-input';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { PillButton } from '@ab-platform/ui/pill-button';
import { SoftBadge } from '@ab-platform/ui/soft-badge';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import {
  confirmAreaDocumentUpload,
  listAreaDocuments,
  signAreaDocumentUpload,
  type AreaDocumentItem,
  type AreaDocumentsView,
} from '@/lib/areaApi';
import { uploadFileToSignedUrl } from '@/lib/api';
import {
  documentsEmptyState,
  documentStateView,
  documentTypeView,
  documentUploadErrorMessage,
  formatSizeLabel,
  missingTone,
  validateFileForUpload,
} from '@/lib/areaDocumentsView';
import { AREA_DOCS_RETURN_TO_SUMMARY } from '@/lib/onboardingUxViews';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; view: AreaDocumentsView };

export function AreaDocumentsPage(): JSX.Element {
  const session = useAreaSession();
  const navigate = useNavigate();
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [busyType, setBusyType] = useState<string | null>(null);
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});

  const reload = useCallback(async (): Promise<void> => {
    const token = await session.getToken();
    if (!token) {
      setLoad({ phase: 'error', message: 'Sessione scaduta: accedi di nuovo.' });
      return;
    }
    const result = await listAreaDocuments(token);
    if (!result.ok) {
      setLoad({
        phase: 'error',
        message: documentUploadErrorMessage(result.kind),
      });
      return;
    }
    setLoad({ phase: 'ready', view: result.data });
  }, [session]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleFile = async (
    item: AreaDocumentItem,
    file: File | undefined,
  ): Promise<void> => {
    if (!file) return;
    const docType = item.documentType;
    setItemErrors((prev) => ({ ...prev, [docType]: '' }));

    const precheck = validateFileForUpload(file);
    if (precheck) {
      setItemErrors((prev) => ({ ...prev, [docType]: precheck }));
      return;
    }

    setBusyType(docType);
    try {
      const token = await session.getToken();
      if (!token) {
        setItemErrors((prev) => ({
          ...prev,
          [docType]: 'Sessione scaduta: accedi di nuovo.',
        }));
        return;
      }
      const signed = await signAreaDocumentUpload(token, {
        documentType: docType,
        originalFilename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      });
      if (!signed.ok) {
        setItemErrors((prev) => ({
          ...prev,
          [docType]: documentUploadErrorMessage(signed.kind),
        }));
        return;
      }
      const put = await uploadFileToSignedUrl(
        signed.data.signedUrl,
        file,
        file.type,
      );
      if (!put.ok) {
        setItemErrors((prev) => ({
          ...prev,
          [docType]: 'Caricamento del file non riuscito. Riprova.',
        }));
        return;
      }
      const confirmed = await confirmAreaDocumentUpload(
        token,
        signed.data.documentId,
      );
      if (!confirmed.ok) {
        setItemErrors((prev) => ({
          ...prev,
          [docType]: documentUploadErrorMessage(confirmed.kind),
        }));
        return;
      }
      await reload();
    } finally {
      setBusyType(null);
    }
  };

  return (
    <FocusedPageShell
      title="Documenti"
      description="Carica i documenti richiesti per la tua richiesta di collaborazione. Puoi sostituirli finché la richiesta è in bozza o quando ti viene chiesta una correzione."
      footer={
        <div className="grid gap-2">
          <p className="text-center text-xs text-muted-foreground">
            {AREA_DOCS_RETURN_TO_SUMMARY.helper}
          </p>
          <PillButton
            type="button"
            size="lg"
            className="w-full"
            onClick={() => navigate(AREA_DOCS_RETURN_TO_SUMMARY.ctaTarget)}
          >
            {AREA_DOCS_RETURN_TO_SUMMARY.ctaLabel}
          </PillButton>
        </div>
      }
    >
      {load.phase === 'loading' ? (
        <Banner tone="info" title="Caricamento">
          Recupero dei documenti in corso…
        </Banner>
      ) : load.phase === 'error' ? (
        <div className="grid gap-3">
          <Banner tone="warning" title="Errore di caricamento">
            {load.message}
          </Banner>
          <div>
            <PillButton
              type="button"
              size="sm"
              onClick={() => {
                setLoad({ phase: 'loading' });
                void reload();
              }}
            >
              Riprova
            </PillButton>
          </div>
        </div>
      ) : documentsEmptyState(
          load.view.requirementsResolved,
          load.view.items.length,
        ).kind !== 'LIST' ? (
        (() => {
          const empty = documentsEmptyState(
            load.view.requirementsResolved,
            load.view.items.length,
          );
          return empty.kind === 'LIST' ? null : (
            <Banner tone="info" title={empty.title}>
              {empty.message}
            </Banner>
          );
        })()
      ) : (
        <div className="grid gap-4">
          {load.view.items.map((item) => (
            <DocumentCard
              key={item.documentType}
              item={item}
              busy={busyType === item.documentType}
              anyBusy={busyType !== null}
              error={itemErrors[item.documentType] || null}
              onFile={(file) => void handleFile(item, file)}
            />
          ))}
        </div>
      )}
    </FocusedPageShell>
  );
}

function DocumentCard({
  item,
  busy,
  anyBusy,
  error,
  onFile,
}: {
  item: AreaDocumentItem;
  busy: boolean;
  anyBusy: boolean;
  error: string | null;
  onFile: (file: File | undefined) => void;
}): JSX.Element {
  const type = documentTypeView(item.documentType);
  const badge =
    item.state === 'MISSING'
      ? missingTone(item.required)
      : documentStateView(item.state);

  return (
    <div className="grid gap-3 rounded-2xl border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="grid gap-0.5">
          <span className="text-sm font-semibold">{type.label}</span>
          {type.description ? (
            <span className="text-xs text-muted-foreground">
              {type.description}
            </span>
          ) : null}
        </div>
        <SoftBadge tone={badge.tone}>{badge.label}</SoftBadge>
      </div>

      {item.uploaded ? (
        <div className="text-xs text-muted-foreground">
          {item.uploaded.displayName} · {formatSizeLabel(item.uploaded.sizeBytes)} ·
          caricato il{' '}
          {new Date(item.uploaded.uploadedAt).toLocaleDateString('it-IT')}
          {item.versionCount > 1 ? ` · versione ${item.versionCount}` : ''}
        </div>
      ) : null}

      {item.correction?.note ? (
        <Banner
          tone={item.correction.required ? 'warning' : 'info'}
          title={
            item.correction.required
              ? 'Correzione richiesta'
              : 'Nota del backoffice'
          }
        >
          {item.correction.note}
        </Banner>
      ) : null}

      {item.canUpload ? (
        <div>
          <FileInput
            triggerLabel={
              busy
                ? 'Caricamento…'
                : item.uploaded
                  ? 'Sostituisci documento'
                  : 'Carica documento'
            }
            accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
            disabled={anyBusy}
            onChange={(e) => {
              onFile(e.target.files?.[0]);
              // Allow re-selecting the same file after an error.
              e.target.value = '';
            }}
          />
        </div>
      ) : null}

      {error ? <FieldError message={error} /> : null}
    </div>
  );
}
